<template>
  <div class="page-container">
    <MainTitle title="Practice. Learn. Repeat. 💬" />
    <div class="app-content-container">
      <div class="hub-wrapper">
        <!-- Primary action cards -->
        <div class="action-cards-row">
          <Card
            class="action-card"
            data-test="new-conversation-btn"
            @click="$router.push({ name: 'ChatSubjectInput' })"
          >
            <template #title>
              <i class="card-icon pi pi-comments" /> New Conversation
            </template>
            <template #content>
              <p class="card-description">
                Start a new chat session on the topics you like.
              </p>
            </template>
          </Card>

          <Card
            class="action-card"
            @click="$router.push('/chat/history')"
          >
            <template #title>
              <i class="card-icon pi pi-history" /> Past Conversations
            </template>
            <template #content>
              <p class="card-description">
                Review summaries and continue learning.
              </p>
            </template>
          </Card>
        </div>

        <!-- Recent conversations -->
        <template v-if="recentConversations.length > 0">
          <div class="section-divider">
            <span class="section-divider-label">Recent Conversations</span>
          </div>
          <div class="recent-list">
            <ConversationCard
              v-for="conversation in recentConversations"
              :key="conversation.id"
              :conversation="conversation"
              @click="openConversation"
              @deleted="removeConversation"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import MainTitle from '@/components/title/MainTitle.vue'
import Card from 'primevue/card'
import ConversationCard from '@/components/chatbot/ConversationCard.vue'
import { fetchPastConversations } from '@/services/chatService'

export default {
  name: 'ChatHub',
  components: { MainTitle, Card, ConversationCard },
  data() {
    return {
      recentConversations: []
    }
  },
  computed: {
    recentSessions() {
      return this.$store.getters['chat/recentSessions']
    }
  },
  async mounted() {
    try {
      const all = await fetchPastConversations()
      this.recentConversations = all.slice(0, 3)
    } catch {
      // silently fail — hub still usable without recent history
    }
  },
  methods: {
    removeConversation(conversationId) {
      this.recentConversations = this.recentConversations.filter(c => c.id !== conversationId)
    },
    openConversation(conversation) {
      this.$router.push({ name: 'ChatSummary', params: { sessionId: conversation.session_id, subject: conversation.scenario_subject } })
    }
  }
}
</script>

<style scoped>
.hub-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  padding: 24px 0;
}

.action-cards-row {
  display: flex;
  gap: 24px;
  width: 100%;
  justify-content: center;
}

.action-card {
  flex: 1;
  cursor: pointer;
  background: var(--color-surface) !important;
  border: 1px solid rgba(27,30,100,0.10);
  border-radius: var(--radius-lg) !important;
  box-shadow: none !important;
  transition: border-color var(--transition-base);
  padding: 20px;
}

.action-card:hover {
  box-shadow: none !important;
  border-color: var(--color-primary-mid) !important;
}

.card-icon {
  margin-right: var(--space-2);
  color: var(--color-primary-mid);
  font-size: var(--font-size-lg);
  vertical-align: middle;
}

.action-card :deep(.p-card-title) {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  font-family: var(--font-family);
}

.card-description {
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
  margin: 0;
}

.recent-section {
  width: 100%;
}

.section-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin-bottom: var(--space-3);
  text-align: left;
}

.empty-state {
  color: var(--color-text-secondary);
  font-style: italic;
  text-align: center;
  padding: var(--space-6) 0;
}

.section-divider {
  display: flex;
  align-items: center;
  width: 100%;
  margin: var(--space-8) 0 var(--space-4);
  gap: var(--space-3);
}

.section-divider::before,
.section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(27,30,100,0.10);
}

.section-divider-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.recent-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
}

.recent-list :deep(.card-inner) {
  padding: var(--space-3) var(--space-4);
  gap: var(--space-1);
}


@media (max-width: 600px) {
  .action-cards-row {
    flex-direction: column;
    align-items: center;
  }

  .action-card {
    width: 100%;
    max-width: 340px;
  }
}
</style>
